import React, { useCallback, useEffect, useState } from 'react';
import DashboardPagination from '../components/DashboardPagination';
import emailOutboxService, {
  type EmailOutboxItem,
  type EmailOutboxFilters,
  type EmailOutboxPage,
  type EmailOutboxStatus,
} from '../services/emailOutboxService';
import { UI_THEME } from '../ui/theme';

const STATUS_LABEL: Record<EmailOutboxStatus, string> = {
  PENDING: 'Pendente',
  PROCESSING: 'Processando',
  RETRY: 'Nova tentativa',
  SENT: 'Enviado',
  EXHAUSTED: 'Esgotado',
  CANCELLED: 'Cancelado',
};

const STATUS_CLASS: Record<EmailOutboxStatus, string> = {
  PENDING: 'bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300',
  PROCESSING: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300',
  RETRY: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  SENT: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  EXHAUSTED: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
  CANCELLED: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
};

const EMPTY_TOTALS = {
  PENDING: 0, PROCESSING: 0, RETRY: 0, SENT: 0, EXHAUSTED: 0, CANCELLED: 0, TOTAL: 0,
};

const EMPTY_FILTERS: EmailOutboxFilters = { status: '', search: '', createdFrom: '', createdTo: '' };

const roleLabel = (role?: string | null): string => {
  switch ((role || '').toUpperCase()) {
    case 'ADMIN': return 'Admin';
    case 'GESTOR': return 'Gestor da Parceria';
    case 'GESTOR_JURIDICO': return 'Gestor Jurídico';
    case 'JURIDICO': return 'Procuradoria Geral Municipal';
    case 'JURIDICO_AUXILIAR': return 'Auxiliar da Procuradoria (PGM)';
    case 'ANALISTA': return 'Analista';
    case 'ORCAMENTO': return 'Orçamento';
    case 'SECRETARIA': return 'Secretaria';
    case 'CONVENIOS': return 'Convênios';
    case 'OPERADOR': return 'Operador';
    case 'CAMARA': return 'Câmara Municipal';
    case 'VEREADOR': return 'Parlamentar';
    default: return '—';
  }
};

const formatDate = (value?: string | null): string => {
  if (!value) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short', timeStyle: 'short',
  }).format(new Date(value));
};

const AdminEmailOutboxPage: React.FC = () => {
  const isSaas = UI_THEME === 'saas';
  const [data, setData] = useState<EmailOutboxPage>({ items: [], total: 0, page: 0, size: 20, totals: EMPTY_TOTALS });
  const [draftFilters, setDraftFilters] = useState<EmailOutboxFilters>(EMPTY_FILTERS);
  const [filters, setFilters] = useState<EmailOutboxFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setData(await emailOutboxService.list(filters, page - 1, pageSize));
    } catch (requestError: any) {
      setError(requestError?.response?.data?.error || 'Não foi possível carregar a fila de e-mails.');
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const interval = window.setInterval(() => { void load(); }, 30000);
    return () => window.clearInterval(interval);
  }, [load]);

  const runAction = async (item: EmailOutboxItem, action: 'retry' | 'cancel') => {
    if (action === 'cancel' && !window.confirm('Cancelar este envio de e-mail?')) return;
    try {
      setWorkingId(item.id);
      setError('');
      setNotice('');
      if (action === 'retry') await emailOutboxService.retry(item.id);
      else await emailOutboxService.cancel(item.id);
      setNotice(action === 'retry' ? 'Reenvio colocado na fila.' : 'Envio cancelado.');
      await load();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.error || 'Não foi possível concluir a ação.');
    } finally {
      setWorkingId(null);
    }
  };

  const applyFilters = (event: React.FormEvent) => {
    event.preventDefault();
    setFilters({ ...draftFilters, search: draftFilters.search.trim() });
    setPage(1);
  };

  const clearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const cards = [
    { label: 'Total', value: data.totals.TOTAL, dot: 'bg-slate-400' },
    { label: 'Pendentes', value: data.totals.PENDING, dot: 'bg-sky-500' },
    { label: 'Retentativas', value: data.totals.RETRY, dot: 'bg-amber-500' },
    { label: 'Esgotados', value: data.totals.EXHAUSTED, dot: 'bg-red-500' },
    { label: 'Enviados', value: data.totals.SENT, dot: 'bg-emerald-500' },
  ];

  return (
    <div className="w-full space-y-6">
      <section className={`rounded-2xl border p-6 shadow-sm ${
        isSaas
          ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/50 to-indigo-50 dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
          : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900'
      }`}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-sky-700 dark:text-sky-400'}`}>
              Administrador
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-950 dark:text-slate-100">Gerenciamento de e-mails</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Acompanhe entregas, erros e retentativas. Destinatários são mascarados e o conteúdo das mensagens não é exibido.
            </p>
          </div>
          <button type="button" onClick={() => void load()} disabled={loading}
            className={`rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${isSaas ? 'bg-teal-600 hover:bg-teal-700' : 'bg-[#1351B4] hover:bg-[#0B3D8F]'}`}>
            Atualizar
          </button>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 sm:grid-cols-5 dark:border-slate-700 dark:bg-slate-700">
          {cards.map((card) => (
            <div key={card.label} className="bg-white px-4 py-3 dark:bg-slate-800">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${card.dot}`} />
                <span className="text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-slate-500 dark:text-slate-400">{card.label}</span>
              </div>
              <p className="mt-2 text-2xl font-bold tabular-nums text-slate-950 dark:text-slate-100">{card.value ?? 0}</p>
            </div>
          ))}
        </div>
      </section>

      {(error || notice) && (
        <div className={`rounded-xl border px-4 py-3 text-sm ${error
          ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300'
          : 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300'}`}>
          {error || notice}
        </div>
      )}

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="border-b border-slate-200 p-4 dark:border-slate-700">
          <form onSubmit={applyFilters} className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(240px,2fr)_minmax(160px,1fr)_150px_150px_auto] xl:items-end">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
              Buscar
              <input type="search" value={draftFilters.search}
                onChange={(event) => setDraftFilters((current) => ({ ...current, search: event.target.value }))}
                placeholder="Destinatário ou assunto"
                className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-950" />
            </label>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
              Situação
              <select value={draftFilters.status}
                onChange={(event) => setDraftFilters((current) => ({ ...current, status: event.target.value as EmailOutboxStatus | '' }))}
                className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-950">
                <option value="">Todas</option>
                {Object.entries(STATUS_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
              Criado de
              <input type="date" value={draftFilters.createdFrom}
                onChange={(event) => setDraftFilters((current) => ({ ...current, createdFrom: event.target.value }))}
                className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-950" />
            </label>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
              Criado até
              <input type="date" value={draftFilters.createdTo} min={draftFilters.createdFrom || undefined}
                onChange={(event) => setDraftFilters((current) => ({ ...current, createdTo: event.target.value }))}
                className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-950" />
            </label>
            <div className="flex gap-2">
              <button type="submit" disabled={loading}
                className={`rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${isSaas ? 'bg-teal-600 hover:bg-teal-700' : 'bg-[#1351B4] hover:bg-[#0B3D8F]'}`}>
                Filtrar
              </button>
              <button type="button" onClick={clearFilters} disabled={loading}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800">
                Limpar
              </button>
            </div>
          </form>
          <p className="mt-3 text-right text-xs text-slate-500 dark:text-slate-400">Atualização automática a cada 30 segundos</p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-700">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3">Criado</th><th className="px-4 py-3">Destinatário / assunto</th>
                <th className="px-4 py-3">Perfil</th>
                <th className="px-4 py-3">Situação</th><th className="px-4 py-3">Tentativas</th>
                <th className="px-4 py-3">Próxima execução / erro</th><th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading && data.items.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">Carregando fila...</td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">Nenhum e-mail encontrado.</td></tr>
              ) : data.items.map((item) => (
                <tr key={item.id} className="align-top hover:bg-slate-50/70 dark:hover:bg-slate-800/50">
                  <td className="whitespace-nowrap px-4 py-4 text-slate-600 dark:text-slate-300">{formatDate(item.createdAt)}</td>
                  <td className="max-w-md px-4 py-4"><div className="font-semibold text-slate-900 dark:text-slate-100">{item.recipient}</div><div className="mt-1 line-clamp-2 text-slate-500 dark:text-slate-400">{item.subject}</div></td>
                  <td className="px-4 py-4 text-slate-600 dark:text-slate-300">{roleLabel(item.profile)}</td>
                  <td className="px-4 py-4"><span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_CLASS[item.status]}`}>{STATUS_LABEL[item.status]}</span></td>
                  <td className="px-4 py-4 text-center font-semibold tabular-nums text-slate-700 dark:text-slate-200">{item.attempts}</td>
                  <td className="max-w-sm px-4 py-4"><div className="text-slate-600 dark:text-slate-300">{formatDate(item.nextAttemptAt)}</div>{item.lastError && <div title={item.lastError} className="mt-1 line-clamp-2 text-xs text-red-600 dark:text-red-300">{item.lastError}</div>}</td>
                  <td className="px-4 py-4"><div className="flex justify-end gap-2">
                    {(item.status === 'EXHAUSTED' || item.status === 'CANCELLED') && <button type="button" disabled={workingId === item.id} onClick={() => void runAction(item, 'retry')} className="rounded-lg border border-sky-300 px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-50 disabled:opacity-50 dark:border-sky-700 dark:text-sky-300 dark:hover:bg-sky-950">Reenviar</button>}
                    {(['PENDING', 'RETRY', 'EXHAUSTED'] as EmailOutboxStatus[]).includes(item.status) && <button type="button" disabled={workingId === item.id} onClick={() => void runAction(item, 'cancel')} className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950">Cancelar</button>}
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DashboardPagination page={page} pageSize={pageSize} totalItems={data.total} onPageChange={setPage}
          onPageSizeChange={(value) => { setPageSize(value); setPage(1); }} itemLabel="e-mails" />
      </section>
    </div>
  );
};

export default AdminEmailOutboxPage;
